/**
 * summaryFlow.js — the rules of the case-summary HUB (s98, v242.242.0).
 *
 * ★★★ WHAT CHANGED. Until s98 this file paced a walk: the summary opened itself, advanced
 * bubble by bubble, stopped before the warning signs, and showed a Submit button at the end.
 * Dr. Rami's mockup C v3 (2026-10-07) replaced the walk with a hub: the Clinical History and
 * three rows — "Conditions closely matching your case and why", "Warning signs and symptoms to
 * seek care right away", "Edit my answers" — and "Continue building my case". The pacing
 * functions (shouldAutoOpen / shouldAutoAdvance / submitCtaVisible / nextStep) went with the
 * walk. What they protected did not:
 *
 * ★★★ THE CLINICAL RULE, RESTATED FOR THE HUB. The walk's rule was "the warning signs always
 *   need a deliberate tap". On the hub they render in ONE place, the warnings view, and the
 *   only way there is the patient tapping its row. showsWarningSigns() is that rule, and
 *   scripts/check-summary-flow.mjs holds ViewSummary to it: the "Watch for these" list renders
 *   only under showsWarningSigns(view), and nothing navigates there on its own.
 *
 * ★★ NOBODY IS STRANDED. A finished analysis that found ZERO conditions must still let the
 *   patient continue (canContinue), exactly as the walk's closing CTA had to.
 *
 * Pure: no React, no store.
 */

export const WARNINGS_VIEW = 'warnings'

/** The warning signs render in this view and no other. */
export function showsWarningSigns(view) {
  return view === WARNINGS_VIEW
}

/**
 * The hub's rows, in order. R22 / D451: a summary built without the AI shows its note and
 * neither the conditions nor the warning-signs row. A summary the patient can no longer edit
 * (describeLocked) offers a note to the doctor instead of "Edit my answers".
 */
export function hubRows({ summaryUnavailable, dxCount, flagCount, describeLocked }) {
  const rows = []
  if (!summaryUnavailable && dxCount > 0) rows.push('conditions')
  if (!summaryUnavailable && flagCount > 0) rows.push('warnings')
  rows.push(describeLocked ? 'note' : 'edit')
  return rows
}

/**
 * May "Continue building my case" be pressed? Not while loading or after a failed generation
 * (the failure card offers the retry). A finished analysis may continue even with zero
 * conditions; a streaming one once its conditions and warning signs have arrived.
 */
export function canContinue({ loading, genFailed, fullResult, dxCount, redFlagsDone }) {
  if (loading || genFailed) return false
  if (fullResult) return true
  return dxCount > 0 && !!redFlagsDone
}

/** The conditions swipe: the next slide index, clamped to [0, total - 1]. */
export function slideStep(i, total, dir) {
  if (!total || total < 1) return 0
  return Math.min(total - 1, Math.max(0, (i || 0) + (dir || 0)))
}
